import { Badge, ExternalLinkIcon, Progress, Separator, Skeleton } from '@commander/ui';
import { useCommanderGoAnalytics } from '../../hooks/useCommanderGoAnalytics.js';
import type { EntityCardBlock, EntityCardField } from '../blocks/entity-card.js';
import { ExpandableBlock, ExpandTrigger } from './ExpandableBlock.js';
import {
  formatValue,
  getProgressColorClass,
  getStatusBadgeVariant,
  sanitizeHref,
  trackDeepLinkClick,
} from './utils.js';

// --- Field Value Renderer ---

function FieldValue({ field }: { field: EntityCardField }) {
  const { value, type, status_color } = field;

  if (type === 'status' && typeof value === 'string') {
    const colorMap = status_color ? { [value]: status_color } : undefined;
    return <Badge variant={getStatusBadgeVariant(value, colorMap)}>{value}</Badge>;
  }

  if (type === 'email' && typeof value === 'string') {
    return (
      <a
        href={`mailto:${value}`}
        className="text-xs font-medium text-primary-700 hover:underline dark:text-primary-300"
      >
        {value}
      </a>
    );
  }

  if (type === 'phone' && typeof value === 'string') {
    return (
      <a
        href={`tel:${value}`}
        className="text-xs font-medium text-primary-700 hover:underline dark:text-primary-300"
      >
        {value}
      </a>
    );
  }

  return <span className="text-xs font-medium">{formatValue(value, type)}</span>;
}

// --- Card Content (shared between inline and expanded) ---

function CardContent({
  data,
  onDeepLinkClick,
}: {
  data: EntityCardBlock;
  onDeepLinkClick?: (href: string) => void;
}) {
  return (
    <div>
      {/* Header */}
      <div className="px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-xs font-semibold">{data.title}</h4>
          <div className="flex items-center gap-1">
            <Badge variant="outline" className="text-[10px] capitalize tracking-wider">
              {data.entity_type}
            </Badge>
            <ExpandTrigger />
          </div>
        </div>
        {data.subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{data.subtitle}</p>}
      </div>

      {/* Fields */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 px-3 pb-2">
        {data.fields.map((field) => (
          <div key={field.label}>
            <p className="text-xs text-muted-foreground">{field.label}</p>
            <FieldValue field={field} />
          </div>
        ))}
      </div>

      {/* Progress */}
      {data.progress && (
        <>
          <Separator />
          <div className="px-3 py-2">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{data.progress.label}</span>
              <span className="text-xs font-medium">{data.progress.value}%</span>
            </div>
            <Progress
              value={data.progress.value}
              className="h-1.5"
              progressBarClass={getProgressColorClass(data.progress.color)}
            />
          </div>
        </>
      )}

      {/* Tags */}
      {data.tags && data.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 px-3 pb-2">
          {data.tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="text-[10px]">
              {tag}
            </Badge>
          ))}
        </div>
      )}

      {/* Actions */}
      {(data.deep_link || (data.actions && data.actions.length > 0)) && (
        <>
          <Separator />
          <div className="flex items-center justify-end gap-2 px-3 py-2">
            {data.actions?.map((action) => {
              const safeHref = sanitizeHref(action.href);
              return safeHref ? (
                <a
                  key={action.href}
                  href={safeHref}
                  onClick={() => onDeepLinkClick?.(safeHref)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline dark:text-primary-300"
                >
                  {action.label}
                  <ExternalLinkIcon className="h-3 w-3" />
                </a>
              ) : (
                <span
                  key={action.href}
                  className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground"
                >
                  {action.label}
                </span>
              );
            })}
            {(() => {
              const safeDeepLink = sanitizeHref(data.deep_link);
              return safeDeepLink && (!data.actions || data.actions.length === 0) ? (
                <a
                  href={safeDeepLink}
                  onClick={() => onDeepLinkClick?.(safeDeepLink)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline dark:text-primary-300"
                >
                  View Details
                  <ExternalLinkIcon className="h-3 w-3" />
                </a>
              ) : null;
            })()}
          </div>
        </>
      )}
    </div>
  );
}

// --- Main EntityCard Component ---

export function EntityCard({ data }: { data: EntityCardBlock }) {
  const analyticsCtx = useCommanderGoAnalytics();
  const handleDeepLinkClick = (href: string) => {
    trackDeepLinkClick(analyticsCtx, 'entity_card', href, data.entity_type);
  };

  return (
    <ExpandableBlock
      title={data.title}
      expandedContent={<CardContent data={data} onDeepLinkClick={handleDeepLinkClick} />}
    >
      <section
        className="rounded-lg border bg-card text-card-foreground"
        aria-label={`${data.entity_type}: ${data.title}`}
      >
        <CardContent data={data} onDeepLinkClick={handleDeepLinkClick} />
      </section>
    </ExpandableBlock>
  );
}

// --- Skeleton ---

export function EntityCardSkeleton() {
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="flex items-center justify-between">
        <Skeleton className="h-4 w-[50%]" />
        <Skeleton className="h-4 w-12" />
      </div>
      <Skeleton className="mt-1 h-3 w-[30%]" />
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2">
        {['a', 'b', 'c', 'd'].map((id) => (
          <div key={id}>
            <Skeleton className="h-3 w-12" />
            <Skeleton className="mt-1 h-3 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}
